import type { CSSProperties, ReactNode } from 'react';
import { Image } from '@chakra-ui/react';
import {
  AdChromeFrame,
  isChromeValidForRatio,
  resolveDefaultChrome,
  useChromeIdentity
} from '../../components/adChrome';
import type { ChromeIdentity, ChromeKind, ChromeDensity } from '../../components/adChrome';
import { HoverPlayVideo } from '../../components/HoverPlayVideo';
import type { ShowcaseAd, ShowcaseCfg } from './types';
import { safeHttpUrl } from '../../lib/safeHref';

export function resolveShowcaseChrome(ad: ShowcaseAd, overrides: Record<string, string>): ChromeKind {
  const raw = overrides[ad.id];
  if (raw && isChromeValidForRatio(raw as ChromeKind, ad.ratio, ad.family)) {
    return raw as ChromeKind;
  }
  return resolveDefaultChrome(ad.ratio, ad.platformFormat, ad.family);
}

export function resolveAdCopy(ad: ShowcaseAd, cfg: ShowcaseCfg): {
  headline: string;
  primary: string;
  description: string;
  cta: string;
  destination: string;
} {
  return {
    headline:    cfg.headline    || ad.copy?.headline    || ad.product.headline    || '',
    primary:     cfg.primary     || ad.copy?.quote       || ad.product.primary     || '',
    description: cfg.description || ad.product.description || '',
    cta:         ad.ctaText || cfg.cta || '',
    destination: ad.ctaUrl || cfg.domain || ''
  };
}

const MEDIA_STYLE: CSSProperties = {
  width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000'
};

export function ShowcaseAdMedia({ ad, mode }: { ad: ShowcaseAd; mode: 'tile' | 'modal' }) {
  // Tiles use the downscaled preview so a many-product grid doesn't stream
  // N full-bitrate sources at once (same convention ProductAds already
  // uses); the modal is the one place that shows the real, full-res asset.
  const src = mode === 'tile' ? (ad.previewUrl || ad.mediaUrl) : ad.mediaUrl;
  if (ad.mediaKind === 'video') {
    if (mode === 'tile') {
      return <HoverPlayVideo src={src} poster={ad.posterUrl} style={MEDIA_STYLE} />;
    }
    return (
      <video
        key={ad.id}
        src={src}
        poster={ad.posterUrl || undefined}
        controls
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        style={MEDIA_STYLE}
      />
    );
  }
  return (
    <Image
      src={src}
      alt=""
      w="100%"
      h="100%"
      objectFit="contain"
      loading={mode === 'tile' ? 'lazy' : undefined}
    />
  );
}

export function ShowcaseChromeFrame({
  ad,
  cfg,
  overrides,
  density = 'full',
  children
}: {
  ad: ShowcaseAd;
  cfg: ShowcaseCfg;
  overrides: Record<string, string>;
  density?: ChromeDensity;
  children: ReactNode;
}) {
  const chrome = resolveShowcaseChrome(ad, overrides);
  // Pass every identity field so useChromeIdentity never falls through
  // to activeBrand (null on the public page; a *different* brand if a
  // logged-in operator opens someone else's snapshot). Then overwrite
  // with cfg so a null logoUrl/domain stays null instead of leaking
  // via `??`. logoUrl is scheme-gated (http/https only) — cfg.logoUrl
  // is operator-supplied and lands in <img src> on this unauthenticated
  // page.
  const logoUrl = safeHttpUrl(cfg.logoUrl);
  const hooked = useChromeIdentity({
    handle:     cfg.handle,
    name:       cfg.brandName,
    logoUrl,
    linkDomain: cfg.domain,
    ctaText:    ad.ctaText || cfg.cta
  });
  const identity: ChromeIdentity = {
    ...hooked,
    handle:     cfg.handle,
    name:       cfg.brandName,
    logoUrl,
    linkDomain: cfg.domain,
    ctaText:    ad.ctaText || cfg.cta
  };
  return (
    <AdChromeFrame
      chrome={chrome}
      ratio={ad.ratio}
      identity={identity}
      density={density}
      showSponsored={cfg.showSponsored !== false}
      showEngagement={cfg.showEngagement !== false}
    >
      {children}
    </AdChromeFrame>
  );
}
